// Pure helpers for the load mileage a payroll batch entry is built from.
// Kept import-free so they stay unit-testable under vitest.

const toMiles = (value) => {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Loaded and empty miles of a single item in CurrentItems.
 *
 * Load items reach CurrentItems in two shapes: rows added during this dialog
 * session (handleFormatPotentialItem) carry mileage both at the top level and
 * under Metadata, while rows restored from a saved batch (batchItems.Items)
 * carry it under Metadata only. Read Metadata first so both shapes total the
 * same — reading the top level alone makes every restored load count as zero.
 *
 * @param {object} item - a CurrentItems entry
 * @returns {{TotalMiles: number, EmptyMiles: number}}
 */
export function getItemMileage(item) {
    return {
        TotalMiles: toMiles(item?.Metadata?.TotalMiles ?? item?.TotalMiles),
        EmptyMiles: toMiles(item?.Metadata?.EmptyMiles ?? item?.EmptyMiles)
    }
}

/**
 * The load items on the entry, one per load. Load pay, accessorial and
 * commission items all describe themselves as "Load #N" and can reference the
 * same load, so keep the first item of each LoadID to avoid counting its
 * mileage twice.
 *
 * @param {object[]} currentItems - the entry's CurrentItems
 * @returns {object[]}
 */
export function getUniqueLoadItems(currentItems) {
    const loadsOnly = (currentItems ?? []).filter(it => it.Description?.startsWith('Load #'))

    return loadsOnly.filter((it, index) =>
        loadsOnly.findIndex(item => item.Metadata?.LoadID === it.Metadata?.LoadID) === index
    )
}

/**
 * Loaded and empty miles across the unique loads on the entry. Drives both the
 * mileage shown on the statement header and the amount of a per mile scheduled
 * deduction, so the two always agree.
 *
 * @param {object[]} currentItems - the entry's CurrentItems
 * @returns {{TotalMiles: number, EmptyMiles: number}}
 */
export function getLoadMileageTotals(currentItems) {
    return getUniqueLoadItems(currentItems).reduce((memo, it) => {
        const mileage = getItemMileage(it)

        return {
            TotalMiles: memo.TotalMiles + mileage.TotalMiles,
            EmptyMiles: memo.EmptyMiles + mileage.EmptyMiles
        }
    }, {TotalMiles: 0, EmptyMiles: 0})
}
